// §10.6: vertical bullet timeline with kind + cycle filters.
// Append-only — there is intentionally no Edit / Delete affordance.

import React from 'react';
import type {
  OpportunityEvent,
  OpportunityEventKind,
} from '../../../types/pipeline';
import {
  CycleFilter,
  EventFilter,
  eventToneClass,
  filterEvents,
} from './state';

interface Props {
  events: OpportunityEvent[];
  filter: EventFilter;
  setFilter: (f: EventFilter) => void;
  cycleFilter: CycleFilter;
  setCycleFilter: (c: CycleFilter) => void;
  collapsedCycles: Record<number, boolean>;
  currentCycle: number;
}

const KIND_OPTIONS: Array<{ value: EventFilter; label: string }> = [
  { value: 'all', label: 'all events' },
  { value: 'open', label: 'opens' },
  { value: 'reopen', label: 'reopens' },
  { value: 'won', label: 'wins' },
  { value: 'lost', label: 'losses' },
];

function fmtTimestamp(iso: string): string {
  const d = new Date(iso);
  const dateStr = d.toLocaleDateString(undefined, {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  });
  const timeStr = d.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  });
  return `${dateStr} · ${timeStr}`;
}

function headlineFor(e: OpportunityEvent): string {
  switch (e.kind) {
    case 'open':
      return e.cycle === 1
        ? `Opportunity created · cycle ${e.cycle}`
        : `Opportunity continues · cycle ${e.cycle}`;
    case 'reopen':
      return `Deal reopened — cycle ${e.cycle} begins`;
    case 'won':
      return `Closed won · cycle ${e.cycle}`;
    case 'lost':
      return `Closed lost · cycle ${e.cycle}`;
  }
}

const DealEventTimeline: React.FC<Props> = ({
  events,
  filter,
  setFilter,
  cycleFilter,
  setCycleFilter,
  collapsedCycles,
  currentCycle,
}) => {
  // events arrive newest-first from the API; the timeline renders that
  // order so newer events are at the top.
  const visible = filterEvents(events, filter, cycleFilter, collapsedCycles);

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="text-[11px] uppercase tracking-wide text-slate-400 font-mono">
          Deal terminal history · append-only
        </div>
        <div className="flex gap-1.5">
          <select
            aria-label="Filter by event kind"
            value={filter}
            onChange={(e) => setFilter(e.target.value as EventFilter)}
            className="bg-white/5 border border-white/10 rounded px-1.5 py-1 text-white text-[11px]"
          >
            {KIND_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <select
            aria-label="Filter by cycle"
            value={cycleFilter}
            onChange={(e) =>
              setCycleFilter(
                e.target.value === 'all' ? 'all' : Number(e.target.value),
              )
            }
            className="bg-white/5 border border-white/10 rounded px-1.5 py-1 text-white text-[11px]"
          >
            <option value="all">all cycles</option>
            {Array.from({ length: currentCycle }, (_, i) => i + 1).map((c) => (
              <option key={c} value={c}>
                cycle {c}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="relative pl-6">
        <div className="absolute left-[7px] top-1.5 bottom-1.5 w-[2px] bg-white/10" />
        {visible.map((e) => (
          <TimelineEntry key={e.id} event={e} />
        ))}
        {visible.length === 0 && (
          <div className="text-xs text-slate-400 py-2">
            No events match the current filters.
          </div>
        )}
      </div>
    </div>
  );
};

const TimelineEntry: React.FC<{ event: OpportunityEvent }> = ({ event }) => {
  const dot = eventToneClass(event.kind);
  return (
    <div className="relative pb-4">
      <div
        className={`absolute -left-[22px] top-1 w-3 h-3 rounded-full ${dot} ring-[3px] ring-bg`}
      />
      <div className="flex items-center gap-2 mb-0.5">
        <span className="text-[9px] font-mono text-slate-500 bg-white/[0.04] px-1.5 py-0.5 rounded">
          cycle {event.cycle}
        </span>
        <span className="text-xs text-slate-200">{headlineFor(event)}</span>
      </div>
      {event.loss_kind && (
        <div className="text-[10px] font-mono text-danger-text mt-0.5">
          loss_kind: {event.loss_kind}
        </div>
      )}
      {event.reason ? (
        <div className="text-xs text-slate-400 italic border-l-2 border-white/[0.16] pl-2 mt-1">
          &ldquo;{event.reason}&rdquo;
        </div>
      ) : null}
      <div className="text-[10px] font-mono text-slate-500 mt-1">
        {fmtTimestamp(event.created_at)}
      </div>
    </div>
  );
};

export default DealEventTimeline;

export type { OpportunityEventKind };
